/* global theme, Shopify */

if (!customElements.get('cart-rebate')) {
  class CartRebate extends HTMLElement {
    static listenersAttached = false;
    static instantSavingCents = 0;
    static lastCartTotalCents = 0;
    static lastDiscountData = { amount: 0, label: 'Instant Rebate' };
    static pendingDiscountData = null;
    static syncedDiscountKey = null;
    static attributeSyncTimer = null;
    static attributeSyncController = null;
    static attributeSyncId = 0;
    static cartUiRefreshInProgress = false;
    /** Once true, deprecated `_discount_amount` has been cleared from the cart. */
    static deprecatedDiscountAmountCleared = false;
    static PRODUCTION_API_URL = 'https://parkave-rebate-engine.vercel.app/api/rebate';
    static LOCAL_API_URL = 'http://localhost:3000/api/rebate';
    static PRODUCTION_INSTANT_API_URL = 'https://parkave-rebate-engine.vercel.app/api/instant-rebate';
    static LOCAL_INSTANT_API_URL = 'http://localhost:3000/api/instant-rebate';
    static DEFAULT_INSTANT_LABEL = 'Instant Rebate';

    /**
     * Toggle storefront instant savings (cart total adjustment + instant rebate UI).
     * @returns {boolean}
     */
    static isInstantRebatesEnabled() {
      return true;
    }

    /**
     * Whether the theme is running in a local development context.
     * @returns {boolean}
     */
    static isLocalDevelopment() {
      const { hostname } = window.location;
      if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
      return Shopify?.theme?.role === 'development';
    }

    /**
     * Mail-in rebate API URL — production engine (local override via LOCAL_API_URL when needed).
     * @returns {string}
     */
    static getApiUrl() {
      return CartRebate.PRODUCTION_API_URL;
    }

    /**
     * Instant rebate API URL — production engine (local override via LOCAL_INSTANT_API_URL when needed).
     * @returns {string}
     */
    static getInstantApiUrl() {
      return CartRebate.PRODUCTION_INSTANT_API_URL;
    }

    constructor() {
      super();
      this.apiUrl = CartRebate.getApiUrl();
      this.instantApiUrl = CartRebate.getInstantApiUrl();
      this.abortController = null;
      this.lastCart = null;
      this.requestId = 0;
    }

    connectedCallback() {
      CartRebate.attachDocumentListeners();
      // Defer one frame so ATC section swaps finish upgrading custom elements
      // before we hit the rebate APIs.
      window.requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.fetchRebates();
      });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    /**
     * Registers document-level cart event listeners once so they survive
     * cart drawer section re-renders.
     */
    static attachDocumentListeners() {
      if (CartRebate.listenersAttached) return;
      CartRebate.listenersAttached = true;

      CartRebate.handleCartEvent = (event) => {
        const cart = event.detail?.cart;
        if (cart && typeof cart.total_price === 'number') {
          CartRebate.lastCartTotalCents = cart.total_price;
        }
        CartRebate.refreshAll(cart);
      };

      document.addEventListener('on:cart:add', CartRebate.handleCartEvent);
      document.addEventListener('on:line-item:change', CartRebate.handleCartEvent);
      // Fetch-only on open (attribute sync is debounced separately and does not
      // refresh cart UI, so this is safe for the minicart).
      document.addEventListener('on:cart-drawer:after-open', CartRebate.handleCartEvent);
    }

    /**
     * Asks every mounted cart-rebate to refetch. Retries briefly when the
     * element is about to mount after an ATC section render.
     * @param {object} [cart] - Optional cart snapshot from a cart event.
     * @param {number} [attempts=8] - Remaining retries while waiting for mount.
     */
    static refreshAll(cart, attempts = 8) {
      const rebateEls = document.querySelectorAll('cart-rebate');
      if (rebateEls.length === 0) {
        if (attempts > 0) {
          window.setTimeout(() => CartRebate.refreshAll(cart, attempts - 1), 50);
        }
        return;
      }
      rebateEls.forEach((rebateEl) => rebateEl.fetchRebates(cart));
    }

    /**
     * Formats cents using the shop money-with-currency format.
     * @param {number} cents - Amount in cents.
     * @returns {string}
     */
    static formatMoneyFromCents(cents, withCurrency = true, noDecimals = false) {
      let format = withCurrency
        ? (theme.settings.moneyWithCurrencyFormat || '${{amount}}')
        : (theme.settings.moneyFormat || '${{amount}}');

      if (noDecimals) {
        format = format.replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}');
      }
      const dollars = cents / 100;
      const amount = dollars.toFixed(2);
      const amountNoDecimals = Math.round(dollars).toString();
      const amountWithComma = amount.replace('.', ',');

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountWithComma)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    /**
     * Persists instant discount amount + label for the Shopify Function.
     * Does not adjust displayed cart totals — the Function already reduces
     * cart.total_price, and subtracting again double-counts the discount.
     * @param {number} instantSavingCents - Instant savings in cents.
     * @param {number} [cartTotalCents] - Shopify cart total in cents.
     * @param {{ amount?: number, label?: string }} [discountData] - Dollar amount + checkout label.
     * @param {{ syncAttributes?: boolean }} [options]
     */
    static applyInstantSavings(instantSavingCents, cartTotalCents, discountData, options = {}) {
      const saving = Math.max(0, Math.round(instantSavingCents));
      const shouldSync = options.syncAttributes !== false;
      CartRebate.instantSavingCents = saving;
      if (typeof cartTotalCents === 'number' && !Number.isNaN(cartTotalCents)) {
        CartRebate.lastCartTotalCents = cartTotalCents;
      }

      const amount = discountData && typeof discountData.amount === 'number'
        ? Math.max(0, discountData.amount)
        : saving / 100;
      const label = (discountData?.label || CartRebate.lastDiscountData.label
        || CartRebate.DEFAULT_INSTANT_LABEL).trim() || CartRebate.DEFAULT_INSTANT_LABEL;

      CartRebate.lastDiscountData = { amount, label };
      CartRebate.updateInstantDiscountBadge(CartRebate.lastDiscountData);

      // Keep data attributes in sync for any listeners, but show Shopify's total
      // as-is (already includes the Function discount after attribute sync).
      document.querySelectorAll('[data-instant-savings-total]').forEach((el) => {
        if (typeof CartRebate.lastCartTotalCents === 'number') {
          el.dataset.cartTotalCents = String(CartRebate.lastCartTotalCents);
        }
        el.classList.remove('cart-total--adjusted');
        delete el.dataset.adjustedTotalCents;
      });

      document.dispatchEvent(new CustomEvent('on:instant-savings:update', {
        bubbles: true,
        detail: {
          instantSavingCents: saving,
          cartTotalCents: CartRebate.lastCartTotalCents,
          adjustedTotalCents: CartRebate.lastCartTotalCents,
          discountData: CartRebate.lastDiscountData
        }
      }));

      if (shouldSync) {
        CartRebate.syncCartAttributes(CartRebate.lastDiscountData);
      }
    }

    /**
     * Hides Shopify bundle and instant-rebate chips. Those amounts are shown in
     * the savings card and the rebate form instead.
     */
    static updateInstantDiscountBadge() {
      document.querySelectorAll('.discount').forEach((el) => {
        const text = (el.textContent || '').toLowerCase();
        const isBundleSavings = text.includes('instant')
          || text.includes('bundle discount')
          || text.includes('park ave bundle');
        if (!isBundleSavings) return;
        const item = el.closest('li') || el;
        item.hidden = true;
      });
    }

    /**
     * Re-renders cart sections so the Shopify Function discount badge/total update.
     * Only runs when line items are already visible — never on an empty drawer.
     * @returns {Promise<void>}
     */
    static async refreshDiscountUi() {
      if (CartRebate.cartUiRefreshInProgress) return;

      const drawer = document.querySelector('cart-drawer');
      const cartItems = drawer?.querySelector('cart-items')
        || document.querySelector('cart-items');

      if (!cartItems || cartItems.dataset.empty === 'true') return;
      if (!cartItems.querySelector('.cart-item')) return;
      if (typeof cartItems.refresh !== 'function') return;

      CartRebate.cartUiRefreshInProgress = true;
      try {
        await cartItems.refresh();
        CartRebate.updateInstantDiscountBadge(CartRebate.lastDiscountData);
        const CartDrawerEl = customElements.get('cart-drawer');
        if (CartDrawerEl?.recalculateCssVarHeights) {
          CartDrawerEl.recalculateCssVarHeights();
        }
      } catch (error) {
        console.warn('[cart-rebate] Could not refresh cart discount UI', error);
      } finally {
        CartRebate.cartUiRefreshInProgress = false;
      }
    }

    /**
     * Builds a stable key for discount attribute sync deduplication.
     * @param {{ amount: number, label: string }} discountData
     * @returns {string}
     */
    static getDiscountSyncKey(discountData) {
      return `${discountData.amount}|${discountData.label}`;
    }

    /**
     * Stores instant discount amount + label on the cart for checkout.
     * Clears attributes when there is no instant discount.
     * Always writes the latest pending payload (not a stale timer closure).
     * @param {{ amount: number, label: string }} discountData
     */
    static syncCartAttributes(discountData) {
      const amount = Math.max(0, Number(discountData?.amount) || 0);
      const label = (discountData?.label || CartRebate.DEFAULT_INSTANT_LABEL).trim()
        || CartRebate.DEFAULT_INSTANT_LABEL;
      CartRebate.pendingDiscountData = { amount, label };

      const syncKey = CartRebate.getDiscountSyncKey(CartRebate.pendingDiscountData);
      if (syncKey === CartRebate.syncedDiscountKey) return;

      clearTimeout(CartRebate.attributeSyncTimer);
      CartRebate.attributeSyncTimer = setTimeout(async () => {
        const payload = CartRebate.pendingDiscountData
          || { amount: 0, label: CartRebate.DEFAULT_INSTANT_LABEL };
        const nextKey = CartRebate.getDiscountSyncKey(payload);
        const needsDeprecatedClear = !CartRebate.deprecatedDiscountAmountCleared;
        // Skip only when instant attrs are already current AND deprecated attr cleared.
        if (nextKey === CartRebate.syncedDiscountKey && !needsDeprecatedClear) return;

        const syncId = CartRebate.attributeSyncId + 1;
        CartRebate.attributeSyncId = syncId;
        if (CartRebate.attributeSyncController) {
          CartRebate.attributeSyncController.abort();
        }
        CartRebate.attributeSyncController = new AbortController();

        try {
          const hasDiscount = payload.amount > 0;
          // Always clear removed `_discount_amount` so existing carts don't carry
          // it into Additional details / any leftover Discount Function.
          const attributes = {
            _instant_saving_cents: hasDiscount ? String(Math.round(payload.amount * 100)) : '',
            _discount_data: hasDiscount ? JSON.stringify(payload) : '',
            _discount_amount: ''
          };
          const response = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ attributes }),
            signal: CartRebate.attributeSyncController.signal
          });
          if (!response.ok) throw new Error(response.status);
          if (syncId !== CartRebate.attributeSyncId) return;
          CartRebate.syncedDiscountKey = nextKey;
          CartRebate.deprecatedDiscountAmountCleared = true;
          // Refresh only when items are already on screen so the Function badge
          // picks up the new amount without wiping an empty/opening drawer.
          await CartRebate.refreshDiscountUi();
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.warn('[cart-rebate] Could not sync instant discount to cart attributes', error);
        }
      }, 400);
    }

    /**
     * Builds checkout discount payload from the instant-rebate API response.
     * @param {object} data - Combined rebate payload (includes instant fields).
     * @param {number} instantTotal - Instant discount in dollars.
     * @returns {{ amount: number, label: string }}
     */
    getInstantDiscountData(data, instantTotal) {
      const programs = data.instant_programs || data.programs || [];
      const label = programs[0]?.label || CartRebate.DEFAULT_INSTANT_LABEL;
      return {
        amount: Math.max(0, Number(instantTotal) || 0),
        label
      };
    }

    /**
     * Returns today's date in YYYY-MM-DD format (local timezone).
     * @returns {string}
     */
    getPurchaseDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    /**
     * Extracts SKU list from cart items, repeating each SKU by line quantity.
     * Used by the mail-in rebate API.
     * @param {object} cart - Cart object from /cart.js.
     * @returns {string[]}
     */
    getSkusFromCart(cart) {
      const skus = [];
      cart.items.forEach((item) => {
        if (!item.sku) return;
        for (let i = 0; i < item.quantity; i += 1) {
          skus.push(item.sku);
        }
      });
      return skus;
    }

    /**
     * Builds instant-rebate request items from cart lines.
     * Price is unit price in dollars (Shopify cart prices are cents).
     * Duplicate SKUs are merged so qty stays accurate after cart edits.
     * @param {object} cart - Cart object from /cart.js.
     * @returns {{ sku: string, price: number, qty: number }[]}
     */
    getItemsFromCart(cart) {
      const bySku = new Map();

      cart.items.forEach((item) => {
        if (!item.sku) return;
        const cents = Number(item.final_price ?? item.price) || 0;
        const price = cents / 100;
        const existing = bySku.get(item.sku);
        if (existing) {
          existing.qty += item.quantity;
          existing.price = price;
          return;
        }
        bySku.set(item.sku, {
          sku: item.sku,
          price,
          qty: item.quantity
        });
      });

      return Array.from(bySku.values());
    }

    /**
     * Formats a dollar amount for display.
     * @param {number} amount - Dollar amount.
     * @returns {string}
     */
    formatMoney(amount, withCurrency = true, noDecimals = false) {
      return CartRebate.formatMoneyFromCents(Math.round(amount * 100), withCurrency, noDecimals);
    }

    /**
     * Escapes HTML special characters.
     * @param {string} str - String to escape.
     * @returns {string}
     */
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /**
     * Whether a rebate line item counts as instant savings.
     * @param {object} rebate - Rebate line item from API.
     * @returns {boolean}
     */
    isInstantRebate(rebate) {
      if (!CartRebate.isInstantRebatesEnabled()) return false;

      const type = rebate.type || rebate.rebateType;
      if (type === 'instant_saving' || type === 'instant_discount') return true;
      if (type === 'bonus_rebate' || type === 'mail_in') return false;

      const name = (rebate.program_name || rebate.programName || '').toLowerCase();
      return name.includes('instant');
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {string}
     */
    getProgramName(rebate) {
      return rebate.program_name || rebate.programName || '';
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {string}
     */
    getFormUrl(rebate) {
      return rebate.form_url || rebate.formUrl || '';
    }

    /**
     * Normalizes API totals from mail-in and instant rebate responses.
     * @param {object} data - Combined rebate API payload.
     * @returns {{ instantTotal: number, mailInTotal: number, rebates: object[], availableRebates: object[] }}
     */
    normalizeRebateData(data) {
      const rebates = data.rebates || [];
      const availableRebates = data.available_rebates || data.availableRebates || [];
      let instantTotal = CartRebate.isInstantRebatesEnabled()
        ? Number(data.instant_discount ?? data.instant_saving ?? data.instantDiscountTotal) || 0
        : 0;
      let mailInTotal = Number(data.bonus_rebates ?? data.mailInTotal) || 0;

      if (!mailInTotal && rebates.length > 0) {
        mailInTotal = rebates
          .filter((rebate) => !this.isInstantRebate(rebate))
          .reduce((sum, rebate) => sum + (Number(rebate.amount) || 0), 0);
      }

      return { instantTotal, mailInTotal, rebates, availableRebates };
    }

    /**
     * @param {object} rebate - Rebate line item from API.
     * @returns {number}
     */
    getRebateAmount(rebate) {
      return Number(rebate.amount) || 0;
    }

    /**
     * Builds HTML for a single rebate form link row.
     * Available-rebate items have no dollar amount — omit the amount label.
     * @param {object} rebate - Rebate or available-rebate line item.
     * @param {boolean} showAmount - Whether to show the money-back amount.
     * @returns {string}
     */
    renderLinkItem(rebate, showAmount) {
      const amount = this.getRebateAmount(rebate);
      const amountHtml = showAmount && amount > 0
        ? `<span class="cart-rebate__link-amount">${this.escapeHtml(this.formatMoney(amount, false, true))} back</span>`
        : '';

      return `
        <li>
          <a class="cart-rebate__link" href="${this.escapeHtml(this.getFormUrl(rebate))}" target="_blank" rel="noopener noreferrer">
            <span class="cart-rebate__link-text">
              <span class="cart-rebate__link-title">${this.escapeHtml(this.getProgramName(rebate))}</span>
              ${amountHtml}
            </span>
            <svg class="cart-rebate__link-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z"/></svg>
          </a>
        </li>`;
    }

    /**
     * Builds HTML for rebate form links (dollar rebates + available promotions).
     * @param {object[]} rebates - Rebate line items with form URLs and amounts.
     * @param {object[]} availableRebates - Available rebate/promo items without amounts.
     * @returns {string}
     */
    renderLinksSection(rebates, availableRebates = []) {
      const rebateLinks = rebates.filter((rebate) => this.getFormUrl(rebate));
      const availableLinks = availableRebates.filter((rebate) => this.getFormUrl(rebate));
      const totalCount = rebateLinks.length + availableLinks.length;
      if (totalCount === 0) return '';

      return `
        <h3 class="cart-rebate__section-title">Rebate Forms (${totalCount})</h3>
        <ul class="cart-rebate__links" role="list">
          ${rebateLinks.map((rebate) => this.renderLinkItem(rebate, true)).join('')}
          ${availableLinks.map((rebate) => this.renderLinkItem(rebate, false)).join('')}
        </ul>
      `;
    }

    /**
     * Builds the rebate card headline (mail-in + optional instant).
     * Example: You're getting $100 back in rebates + $240 Instant rebate (see cart)
     * @param {number} mailInTotal - Mail-in total in dollars.
     * @param {number} instantTotal - Instant discount in dollars.
     * @returns {string}
     */
    buildHeadlineHtml(mailInTotal, instantTotal) {
      const mailIn = Math.max(0, Number(mailInTotal) || 0);
      const instant = Math.max(0, Number(instantTotal) || 0);
      const mailInAmount = this.escapeHtml(this.formatMoney(mailIn, false, true));
      const instantAmount = this.escapeHtml(this.formatMoney(instant, false, true));
      const instantLine = `<span class="cart-rebate__instant-line"><span class="cart-rebate__amount">${instantAmount}</span> Instant rebate (see cart)</span>`;

      if (mailIn > 0 && instant > 0) {
        return `You're getting <span class="cart-rebate__amount">${mailInAmount}</span> back in rebates<span class="cart-rebate__plus"> +</span>${instantLine}`;
      }
      if (mailIn > 0) {
        return `You're getting <span class="cart-rebate__amount">${mailInAmount}</span> back in rebates`;
      }
      if (instant > 0) {
        return `You're getting ${instantLine}`;
      }
      return '';
    }

    /**
     * Renders rebate card UI. Mail-in totals come from /api/rebate; instant from
     * /api/instant-rebate (also synced to checkout via `_discount_data`).
     * @param {object} data - Combined rebate API payload.
     * @param {object} cart - Cart object from /cart.js.
     */
    render(data, cart) {
      const { instantTotal, mailInTotal, rebates, availableRebates } = this.normalizeRebateData(data);
      const mailInRebates = rebates.filter((rebate) => !this.isInstantRebate(rebate));
      const hasAvailableLinks = availableRebates.some((rebate) => this.getFormUrl(rebate));
      const hasAvailableOnly = mailInTotal <= 0 && instantTotal <= 0 && hasAvailableLinks;
      const discountData = CartRebate.isInstantRebatesEnabled()
        ? this.getInstantDiscountData(data, instantTotal)
        : { amount: 0, label: CartRebate.DEFAULT_INSTANT_LABEL };
      const instantSavingCents = Math.round(discountData.amount * 100);

      CartRebate.applyInstantSavings(instantSavingCents, cart.total_price, discountData);

      if (mailInTotal <= 0 && instantTotal <= 0 && !hasAvailableLinks) {
        this.hide();
        return;
      }

      const linksSectionHtml = this.renderLinksSection(mailInRebates, availableRebates);
      const headlineHtml = this.buildHeadlineHtml(mailInTotal, instantTotal);

      if (hasAvailableOnly) {
        this.innerHTML = `
          <div class="cart-rebate__card">
            <div class="cart-rebate__header">
              <div class="cart-rebate__badge">Rebates/Promotions Available</div>
            </div>
            <div class="cart-rebate__body">
              ${linksSectionHtml}
              <p class="cart-rebate__disclaimer">
                View Rebate Forms for final details.
              </p>
            </div>
          </div>
        `;
      } else {
        this.innerHTML = `
          <div class="cart-rebate__card">
            <div class="cart-rebate__header">
              <div class="cart-rebate__badge">Rebates Unlocked</div>
              ${headlineHtml ? `<p class="cart-rebate__headline">${headlineHtml}</p>` : ''}
            </div>
            <div class="cart-rebate__body">
              ${linksSectionHtml}
              <p class="cart-rebate__disclaimer">
                ${mailInTotal > 0
                  ? 'Estimated total. Submit each form online to claim — final amounts are confirmed on the official rebate forms.'
                  : 'Instant rebate is applied in your cart total at checkout.'}
              </p>
            </div>
          </div>
        `;
      }

      this.hidden = false;
      this.dispatchHeightUpdate();
    }

    /**
     * Hides the rebate section and clears its content.
     */
    hide() {
      this.hidden = true;
      this.innerHTML = '';
      this.dispatchHeightUpdate();
    }

    /**
     * Clears rebate UI and resets adjusted totals.
     * @param {object} [cart] - Cart object from /cart.js.
     */
    clearRebates(cart) {
      this.hide();
      CartRebate.applyInstantSavings(0, cart?.total_price ?? 0, {
        amount: 0,
        label: CartRebate.DEFAULT_INSTANT_LABEL
      });
    }

    /**
     * Notifies the cart drawer to recalculate sticky summary heights.
     */
    dispatchHeightUpdate() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Fetches mail-in rebate data for the cart SKUs.
     * @param {string[]} skus - Expanded SKU list.
     * @param {AbortSignal} signal - Fetch abort signal.
     * @returns {Promise<object>}
     */
    async fetchMailInRebates(skus, signal) {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          skus,
          purchaseDate: this.getPurchaseDate()
        }),
        signal
      });

      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    /**
     * Fetches instant rebate discounts for cart line items.
     * @param {{ sku: string, price: number, qty: number }[]} items - Cart items.
     * @param {AbortSignal} signal - Fetch abort signal.
     * @returns {Promise<object>}
     */
    async fetchInstantRebates(items, signal) {
      const response = await fetch(this.instantApiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items }),
        signal
      });

      if (!response.ok) throw new Error(response.status);
      return response.json();
    }

    /**
     * Fetches cart data and loads mail-in + instant rebate responses.
     * Always reloads /cart.js so rapid add-to-cart updates use the latest lines.
     * Stale responses from older requests on this element are ignored.
     * Request ids are per element so the cart page and minicart can both render.
     * @param {object} [cart] - Optional cart snapshot (total used until /cart.js returns).
     */
    async fetchRebates(cart) {
      const requestId = this.requestId + 1;
      this.requestId = requestId;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      try {
        if (cart && typeof cart.total_price === 'number') {
          CartRebate.lastCartTotalCents = cart.total_price;
        }

        const cartResponse = await fetch(`${theme.routes.cart}.js`, {
          signal,
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error(cartResponse.status);
        cart = await cartResponse.json();

        if (requestId !== this.requestId) return;

        this.lastCart = cart;

        if (!cart.items || cart.items.length === 0) {
          this.clearRebates(cart);
          return;
        }

        const skus = this.getSkusFromCart(cart);
        const items = this.getItemsFromCart(cart);
        if (skus.length === 0) {
          this.clearRebates(cart);
          return;
        }

        const mailInPromise = this.fetchMailInRebates(skus, signal).catch((error) => {
          if (error.name === 'AbortError') throw error;
          console.warn('[cart-rebate] Mail-in rebate request failed', error);
          return { rebates: [], available_rebates: [], bonus_rebates: 0 };
        });
        const instantPromise = CartRebate.isInstantRebatesEnabled()
          ? this.fetchInstantRebates(items, signal).catch((error) => {
            if (error.name === 'AbortError') throw error;
            console.warn('[cart-rebate] Instant rebate request failed', error);
            return { instant_discount: 0, programs: [], lines: [] };
          })
          : Promise.resolve({ instant_discount: 0, programs: [], lines: [] });

        const [mailInData, instantData] = await Promise.all([mailInPromise, instantPromise]);

        if (requestId !== this.requestId) return;

        this.render({
          ...mailInData,
          instant_discount: Number(instantData.instant_discount) || 0,
          instant_programs: instantData.programs || [],
          instant_lines: instantData.lines || []
        }, cart);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestId !== this.requestId) return;
        this.clearRebates(this.lastCart);
      }
    }
  }

  customElements.define('cart-rebate', CartRebate);
  // Listen even before a <cart-rebate> mounts (e.g. first item added to an empty cart).
  CartRebate.attachDocumentListeners();
}
